import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { useCart } from '../cart.jsx';

const fmtOpts = (o) => Object.entries(o || {}).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · ');

export default function Checkout() {
  const { items, remove, clear, total } = useCart();
  const [settings, setSettings] = useState(null);
  const [tipoEntrega, setTipoEntrega] = useState('recogida');
  const [form, setForm] = useState({ nombre: '', telefono: '', email: '', direccion: '', fechaEntrega: '', ventana: '', notas: '' });
  const [alergenos, setAlergenos] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => { api.settings().then(setSettings).catch(() => {}); }, []);

  // fecha mínima según antelación
  const minFecha = useMemo(() => {
    const h = settings?.antelacionHoras?.personalizado ?? 72;
    const d = new Date(Date.now() + h * 3600 * 1000);
    return d.toISOString().slice(0, 10);
  }, [settings]);

  const gastosEnvio = useMemo(() => {
    if (tipoEntrega !== 'envio' || !settings) return 0;
    return total >= (settings.envioGratisDesde ?? 60) ? 0 : (settings.tarifaEnvio ?? 5);
  }, [tipoEntrega, total, settings]);

  const minimo = settings?.pedidoMinimo ?? 15;
  const totalFinal = total + gastosEnvio;

  const submit = async () => {
    setErr('');
    if (!form.nombre || !form.telefono) return setErr('Indica tu nombre y teléfono.');
    if (!form.fechaEntrega) return setErr('Elige una fecha de entrega.');
    if (tipoEntrega === 'envio' && !form.direccion) return setErr('Indica la dirección de envío.');
    if (!alergenos) return setErr('Debes aceptar el aviso de alérgenos.');
    if (total < minimo) return setErr(`El pedido mínimo es ${minimo} €.`);
    setSending(true);
    try {
      const body = {
        cliente: { nombre: form.nombre, telefono: form.telefono, email: form.email },
        items: items.map((i) => ({ slug: i.slug, opcionesElegidas: i.opcionesElegidas, textoPersonalizado: i.textoPersonalizado, cantidad: i.cantidad })),
        tipoEntrega, direccion: form.direccion, fechaEntrega: form.fechaEntrega, ventana: form.ventana,
        alergenosAceptado: true, notas: form.notas
      };
      const res = await api.createOrder(body);
      setDone({ ...res, telefono: form.telefono });
      clear();
    } catch (e) { setErr(e.message); }
    setSending(false);
  };

  if (done) {
    const wa = settings?.whatsapp;
    const msg = encodeURIComponent(`¡Hola Kanel! Acabo de hacer el pedido ${done.codigo} en la web 🧁`);
    return (
      <div className="page narrow">
        <div className="confirm">
          <div className="confirm-icon">🎉</div>
          <h1>¡Pedido recibido!</h1>
          <p>Tu código de pedido es <b>{done.codigo}</b>. Total estimado: <b>{done.total.toFixed(2)} €</b>.</p>
          <p>Te confirmaremos la disponibilidad para tu fecha por WhatsApp o teléfono lo antes posible.</p>
          {wa && <a className="btn btn--primary" href={`https://wa.me/${wa}?text=${msg}`} target="_blank" rel="noreferrer">Confirmar por WhatsApp</a>}
          <Link to={`/seguimiento?codigo=${encodeURIComponent(done.codigo)}&telefono=${encodeURIComponent(done.telefono || '')}`} className="btn btn--ghost">Ver estado del pedido</Link>
          <Link to="/carta" className="btn btn--ghost">Seguir viendo la carta</Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="page narrow">
        <h1>Tu pedido está vacío</h1>
        <p className="empty">Añade algún dulce desde la carta para empezar.</p>
        <Link to="/carta" className="btn btn--primary">Ver la carta</Link>
      </div>
    );
  }

  return (
    <div className="page checkout">
      <h1>Tu pedido</h1>
      <div className="checkout-grid">
        <div className="cart-list">
          {items.map((i) => (
            <div key={i._k} className="cart-row">
              <div className="cart-thumb" style={{ backgroundImage: `url(${i.imagen})` }} />
              <div className="cart-info">
                <h4>{i.cantidad}× {i.nombre}</h4>
                <p>{fmtOpts(i.opcionesElegidas)}{i.textoPersonalizado && ` · "${i.textoPersonalizado}"`}</p>
              </div>
              <div className="cart-price">{(i.precioUnidad * i.cantidad).toFixed(2)} €</div>
              <button className="cart-del" onClick={() => remove(i._k)}>×</button>
            </div>
          ))}
        </div>

        <div className="checkout-form">
          <h3>Datos de entrega</h3>

          <div className="seg">
            <button className={tipoEntrega === 'recogida' ? 'seg-b on' : 'seg-b'} onClick={() => setTipoEntrega('recogida')}>Recogida</button>
            <button className={tipoEntrega === 'envio' ? 'seg-b on' : 'seg-b'} onClick={() => setTipoEntrega('envio')}>Envío en Madrid</button>
          </div>

          <label>Nombre*<input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></label>
          <label>Teléfono / WhatsApp*<input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></label>
          <label>Email<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
          {tipoEntrega === 'envio' && (
            <label>Dirección de envío*<input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} placeholder="Calle, número, Madrid" /></label>
          )}
          <label>Fecha de {tipoEntrega === 'envio' ? 'entrega' : 'recogida'}*
            <input type="date" min={minFecha} value={form.fechaEntrega} onChange={(e) => setForm({ ...form, fechaEntrega: e.target.value })} />
            <small>Necesitamos al menos {Math.round((settings?.antelacionHoras?.personalizado ?? 72) / 24)} días para los productos personalizados.</small>
          </label>
          <label>Franja horaria
            <select value={form.ventana} onChange={(e) => setForm({ ...form, ventana: e.target.value })}>
              <option value="">Indiferente</option>
              {(settings?.ventanas || []).map((v) => <option key={v} value={v}>{v} h</option>)}
            </select>
          </label>
          <label>Notas / personalización<textarea rows="3" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} placeholder="Cuéntanos colores, temática, alergias, etc." /></label>

          <label className="check">
            <input type="checkbox" checked={alergenos} onChange={(e) => setAlergenos(e.target.checked)} />
            <span>Entiendo que los productos pueden contener gluten, lácteos, huevo y frutos secos, y se elaboran en un obrador donde se manipulan estos alérgenos.</span>
          </label>

          <div className="totals">
            <div><span>Subtotal</span><b>{total.toFixed(2)} €</b></div>
            {tipoEntrega === 'envio' && <div><span>Envío</span><b>{gastosEnvio === 0 ? 'Gratis' : gastosEnvio.toFixed(2) + ' €'}</b></div>}
            <div className="grand"><span>Total</span><b>{totalFinal.toFixed(2)} €</b></div>
          </div>

          {err && <p className="form-err">{err}</p>}
          <button className="btn btn--primary btn--block" disabled={sending} onClick={submit}>
            {sending ? 'Enviando…' : 'Enviar solicitud de pedido'}
          </button>
          <p className="note">No se cobra nada online. Te confirmamos disponibilidad y forma de pago por WhatsApp.</p>
        </div>
      </div>
    </div>
  );
}
